/**
 * buy-box-sync.js
 *
 * Sync bridge between the buy boxes that may share one product section/form:
 *   SPL   <selling-plans-alt>
 *   NBB   <norse-buy-box>
 *   NSBB  <norse-subscription-buy-box>
 *   NSTBB <norse-subscription-toggle-buy-box>
 *   NBBB  <norse-bundles-buy-box>
 *
 * Guarantees:
 *   1. Exactly ONE box owns the authoritative hidden selling_plan input per form.
 *      Authority order: NBBB (intercepts submit itself) > NBB > NSBB > NSTBB > SPL.
 *      Demoted boxes get their input's name removed so Shopify never receives
 *      duplicate/conflicting selling_plan values (which adds the wrong product/plan).
 *   2. Plan type (subscribe vs one-time), variant and delivery frequency stay
 *      visually in sync across all UIs regardless of which one the customer uses.
 *   3. Each box's internal methods are called for state changes (canonical plan ID
 *      resolution, price hydration, button text) so no cart logic is duplicated.
 *
 * Load order: must run AFTER the buy box component scripts.
 * All scripts are defer="defer" so load order follows DOM order.
 */

(function () {
  'use strict';

  // The script tag may be rendered by more than one snippet — run once only.
  if (window.__norseBuyBoxSyncLoaded) return;
  window.__norseBuyBoxSyncLoaded = true;

  /** Exact cents in the market's punctuation — see NorseSellingPlanUtils.formatMoney. */
  function formatMoney(cents) {
    var shared = window.NorseSellingPlanUtils && window.NorseSellingPlanUtils.formatMoney;
    if (shared) return shared(cents);
    return (Math.round(Number(cents) || 0) / 100).toFixed(2).replace(/\.00$/, '');
  }

  // Re-entrancy guard — prevents listener A triggering component → listener B → loop
  let _depth = 0;
  function guard(fn) {
    if (_depth > 0) return;
    _depth++;
    try { fn(); } finally { _depth--; }
  }

  // ─────────────────────────────────────────────────────────────
  //  Bootstrap
  // ─────────────────────────────────────────────────────────────

  function initPairs() {
    // Collect every buy box on the page, grouped by section.
    var sections = {};
    function collect(selector, key) {
      document.querySelectorAll(selector + '[data-section-id]').forEach(function (el) {
        var sid = el.dataset.sectionId;
        if (!sections[sid]) sections[sid] = {};
        if (!sections[sid][key]) sections[sid][key] = el;
      });
    }
    collect('selling-plans-alt', 'spl');
    collect('norse-buy-box', 'nbb');
    collect('norse-subscription-buy-box', 'nsbb');
    collect('norse-subscription-toggle-buy-box', 'nstbb');
    collect('norse-bundles-buy-box', 'nbbb');

    Object.keys(sections).forEach(function (sid) {
      var s = sections[sid];

      // Original SPL ↔ NBB pair
      if (s.spl && s.nbb) setupPair(s.spl, s.nbb);

      // NSTBB combos — demote inputs so only one box owns selling_plan
      if (s.nstbb) {
        if (s.nbbb) {
          // Bundles box intercepts submit and builds its own /cart/add.js payload —
          // remove every other selling_plan input on this form to be safe.
          demoteInput(s.nstbb, '[data-nstbb-selling-plan-input]');
        } else if (s.nbb) {
          demoteInput(s.nstbb, '[data-nstbb-selling-plan-input]');
          setupNstbbWithNbb(s.nstbb, s.nbb);
          if (s.spl) wireSplPlanTypeToNstbb(s.spl, s.nstbb);
        } else if (s.nsbb) {
          demoteInput(s.nstbb, '[data-nstbb-selling-plan-input]');
          setupNstbbWithNsbb(s.nstbb, s.nsbb);
        } else if (s.spl) {
          if (s.spl.classList.contains('style-5')) {
            /* Checkbox box owns selling_plan. Hidden NSTBB must not keep a sub id. */
            demoteInput(s.nstbb, '[data-nstbb-selling-plan-input]');
          } else {
            // NSTBB is authoritative — demote SPL (mirrors the NBB+SPL strategy)
            demoteInput(s.spl, '[data-selling-plan-input]');
            setupNstbbWithSpl(s.nstbb, s.spl);
          }
        }
      }
    });
  }

  /** Remove a hidden input's name so it never serializes into the form. */
  function demoteInput(root, selector) {
    var input = root.querySelector(selector);
    if (input) input.removeAttribute('name');
  }

  // ─────────────────────────────────────────────────────────────
  //  Pair wiring
  // ─────────────────────────────────────────────────────────────

  function setupPair(spl, nbb) {
    // NBB is authoritative for selling_plan — remove SPL's input from the form
    // so Shopify never receives two conflicting selling_plan values.
    var splPlanInput = spl.querySelector('[data-selling-plan-input]');
    if (splPlanInput) splPlanInput.removeAttribute('name');

    // ── SPL → NBB ────────────────────────────────────────────────

    // Plan type radio (subscribe / one-time)
    spl.querySelectorAll('.selling-plans-alt__radio-input').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        guard(function () { syncPlanTypeToNBB(nbb, Boolean(radio.value)); });
      });
    });

    // Delivery frequency radio
    spl.querySelectorAll('.selling-plans-alt__delivery-input').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        guard(function () {
          syncDeliveryToNBB(nbb, radio.dataset.sellingPlanId || radio.value);
        });
      });
    });

    // Variant radio (subscription and one-time columns)
    spl.querySelectorAll('.selling-plans-alt__variant-input').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        guard(function () {
          syncVariantToNBB(nbb, radio.dataset.variantId || radio.value);
        });
      });
    });

    // ── NBB → SPL ────────────────────────────────────────────────
    // rAF ensures NBB's own click handler runs first so we read updated state.

    nbb.querySelectorAll('.nbb__variant-option').forEach(function (card) {
      card.addEventListener('click', function () {
        requestAnimationFrame(function () {
          guard(function () {
            syncVariantToSPL(spl, card.dataset.variantId);
            var selDel = nbb.querySelector('.nbb__delivery-option.is-selected');
            if (selDel) syncDeliveryToSPL(spl, selDel.dataset.sellingPlanId);
          });
        });
      });
    });

    nbb.querySelectorAll('.nbb__delivery-option').forEach(function (opt) {
      // Note: NBB calls stopPropagation() so parent plan-box click won't also fire.
      opt.addEventListener('click', function () {
        requestAnimationFrame(function () {
          guard(function () {
            syncDeliveryToSPL(spl, opt.dataset.sellingPlanId);
            syncPlanTypeToSPL(spl, true); // delivery click always means subscription
          });
        });
      });
    });

    var subPlanBox = nbb.querySelector('[data-nbb-plan="sub"]');
    if (subPlanBox) {
      subPlanBox.addEventListener('click', function () {
        requestAnimationFrame(function () { guard(function () { syncPlanTypeToSPL(spl, true); }); });
      });
    }

    var otPlanBox = nbb.querySelector('[data-nbb-plan="ot"]');
    if (otPlanBox) {
      otPlanBox.addEventListener('click', function () {
        requestAnimationFrame(function () { guard(function () { syncPlanTypeToSPL(spl, false); }); });
      });
    }

    // ── Initial state: push NBB's SSR state into SPL ─────────────
    requestAnimationFrame(function () {
      var selCard = nbb.querySelector('.nbb__variant-option.is-selected');
      if (selCard && selCard.dataset.variantId) {
        syncVariantToSPL(spl, selCard.dataset.variantId);
      }
      var selDel = nbb.querySelector('.nbb__delivery-option.is-selected');
      if (selDel && selDel.dataset.sellingPlanId) {
        syncDeliveryToSPL(spl, selDel.dataset.sellingPlanId);
      }
      syncPlanTypeToSPL(spl, nbb.isSubscriptionSelected !== false);
    });
  }

  // ─────────────────────────────────────────────────────────────
  //  NSTBB pair wiring
  // ─────────────────────────────────────────────────────────────

  /** Toggle NSTBB's visual state without re-triggering its own handlers. */
  function nstbbSetPlanSilent(nstbb, isSub) {
    nstbb.classList.toggle('is-sub', isSub);
    nstbb.classList.toggle('is-ot', !isSub);
    var subBtn = nstbb.querySelector('[data-nstbb-toggle="sub"]');
    var otBtn  = nstbb.querySelector('[data-nstbb-toggle="ot"]');
    if (subBtn) subBtn.setAttribute('aria-pressed', String(isSub));
    if (otBtn)  otBtn.setAttribute('aria-pressed', String(!isSub));
  }

  /** Keep hidden NSTBB toggle visuals aligned when SPL is the visible buy box. */
  function wireSplPlanTypeToNstbb(spl, nstbb) {
    spl.querySelectorAll('.selling-plans-alt__radio-input').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        guard(function () { nstbbSetPlanSilent(nstbb, Boolean(radio.value)); });
      });
    });
  }

  /**
   * NSTBB + NBB — NBB stays authoritative for selling_plan (it has variant cards
   * and delivery frequencies). NSTBB mirrors the plan type both ways.
   */
  function setupNstbbWithNbb(nstbb, nbb) {
    // NSTBB toggle → NBB plan type
    nstbb.querySelectorAll('[data-nstbb-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        requestAnimationFrame(function () {
          guard(function () { syncPlanTypeToNBB(nbb, btn.dataset.nstbbToggle === 'sub'); });
        });
      });
    });

    // NBB plan boxes / delivery clicks → NSTBB toggle visual
    var subBox = nbb.querySelector('[data-nbb-plan="sub"]');
    var otBox  = nbb.querySelector('[data-nbb-plan="ot"]');
    if (subBox) {
      subBox.addEventListener('click', function () {
        requestAnimationFrame(function () { guard(function () { nstbbSetPlanSilent(nstbb, true); }); });
      });
    }
    if (otBox) {
      otBox.addEventListener('click', function () {
        requestAnimationFrame(function () { guard(function () { nstbbSetPlanSilent(nstbb, false); }); });
      });
    }
    nbb.querySelectorAll('.nbb__delivery-option').forEach(function (opt) {
      opt.addEventListener('click', function () {
        requestAnimationFrame(function () { guard(function () { nstbbSetPlanSilent(nstbb, true); }); });
      });
    });

    // Initial: push NBB's SSR state into NSTBB
    requestAnimationFrame(function () {
      guard(function () { nstbbSetPlanSilent(nstbb, nbb.isSubscriptionSelected !== false); });
    });
  }

  /**
   * NSTBB + SPL — NSTBB is authoritative (SPL's input is demoted, mirroring the
   * NBB+SPL strategy). Plan type syncs both ways; if the customer picks a
   * different delivery frequency in SPL, NSTBB adopts that plan ID.
   */
  function setupNstbbWithSpl(nstbb, spl) {
    // SPL plan-type radios → NSTBB
    spl.querySelectorAll('.selling-plans-alt__radio-input').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        guard(function () {
          if (typeof nstbb._selectPlan === 'function') {
            nstbb._selectPlan(radio.value ? 'sub' : 'ot');
          }
        });
      });
    });

    // SPL delivery frequency → NSTBB canonical selling_plan (bundle variants use different ids)
    spl.querySelectorAll('.selling-plans-alt__delivery-input').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        guard(function () {
          if (nstbb.classList.contains('is-sub') && typeof nstbb._resolveAndSyncPlanId === 'function') {
            nstbb._resolveAndSyncPlanId();
          }
        });
      });
    });

    // SPL variant change → re-resolve plan for the new variant's allocation slot
    spl.querySelectorAll('.selling-plans-alt__variant-input').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        guard(function () {
          if (nstbb.classList.contains('is-sub') && typeof nstbb._resolveAndSyncPlanId === 'function') {
            nstbb._resolveAndSyncPlanId();
          }
        });
      });
    });

    // NSTBB toggle → SPL visuals
    nstbb.querySelectorAll('[data-nstbb-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        requestAnimationFrame(function () {
          guard(function () { syncPlanTypeToSPL(spl, btn.dataset.nstbbToggle === 'sub'); });
        });
      });
    });

    // Initial: push NSTBB's SSR state (subscription by default) into SPL
    requestAnimationFrame(function () {
      guard(function () {
        syncPlanTypeToSPL(spl, nstbb.classList.contains('is-sub'));
        if (nstbb.classList.contains('is-sub') && typeof nstbb._resolveAndSyncPlanId === 'function') {
          nstbb._resolveAndSyncPlanId();
        }
      });
    });
  }

  /**
   * NSTBB + NSBB — NSBB owns the selling_plan input because its rows carry
   * per-row variant + plan-slot combinations (a row may use a non-first plan).
   * NSTBB's toggle decides subscription vs one-time:
   *   - Buy once  → blank NSBB's input (one-time purchase of the row's variant)
   *   - Subscribe → restore the selected row's plan (canonical resolution incl.)
   * Row pricing is mirrored into NSTBB so its big price matches the actual plan.
   */
  function setupNstbbWithNsbb(nstbb, nsbb) {
    // NSTBB toggle → NSBB input
    nstbb.querySelectorAll('[data-nstbb-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        requestAnimationFrame(function () {
          guard(function () {
            var isSub = btn.dataset.nstbbToggle === 'sub';
            if (isSub) {
              if (typeof nsbb._syncFormFromSelected === 'function') nsbb._syncFormFromSelected();
              if (typeof nsbb._resolveAndSyncPlanId === 'function') nsbb._resolveAndSyncPlanId();
              if (typeof nsbb._updateButtonText === 'function') nsbb._updateButtonText();
              applyNsbbPricingToNstbb(nstbb, nsbb);
            } else if (typeof nsbb._writeSellingPlanInput === 'function') {
              nsbb._writeSellingPlanInput('');
            }
          });
        });
      });
    });

    // NSBB row click → NSTBB shows subscription state + the row's pricing
    nsbb.querySelectorAll('[data-nsbb-option]').forEach(function (row) {
      row.addEventListener('click', function () {
        requestAnimationFrame(function () {
          guard(function () {
            nstbbSetPlanSilent(nstbb, true);
            applyNsbbPricingToNstbb(nstbb, nsbb);
          });
        });
      });
    });

    // NSTBB rewrites slot-0 prices on variant/form changes — re-apply the row's
    // pricing afterwards so the NSBB row stays the source of truth.
    document.addEventListener('variant:change', function () {
      requestAnimationFrame(function () {
        guard(function () { applyNsbbPricingToNstbb(nstbb, nsbb); });
      });
    });

    // Initial: NSTBB defaults to subscription — adopt the default row's pricing
    requestAnimationFrame(function () {
      guard(function () { applyNsbbPricingToNstbb(nstbb, nsbb); });
    });
  }

  /** Mirror the selected NSBB row's line pricing into NSTBB's price header. */
  function applyNsbbPricingToNstbb(nstbb, nsbb) {
    if (!nstbb.classList.contains('is-sub')) return;
    var row = nsbb.querySelector('[data-nsbb-option].is-selected');
    if (!row) return;

    var subR = parseInt(row.dataset.subLine || '0', 10);
    var cmpR = parseInt(row.dataset.cmpLine || '0', 10);
    if (subR <= 0) return;
    var saveR = cmpR - subR;

    var priceEl = nstbb.querySelector('[data-nstbb-price]');
    if (priceEl) priceEl.textContent = formatMoney(subR);

    var cmpEl = nstbb.querySelector('[data-nstbb-compare]');
    if (cmpEl) {
      cmpEl.textContent = formatMoney(cmpR);
      cmpEl.classList.toggle('is-hidden', cmpR <= subR);
    }

    var badgeEl = nstbb.querySelector('[data-nstbb-save-badge]');
    if (badgeEl) {
      var pct = cmpR > 0 ? Math.round((saveR / cmpR) * 100) : 0;
      badgeEl.textContent = pct > 0 ? 'SAVE ' + pct + '%' : '';
      badgeEl.classList.toggle('is-hidden', pct <= 0);
    }

    var amountEl = nstbb.querySelector('[data-nstbb-savings-amount]');
    if (amountEl) amountEl.textContent = formatMoney(saveR);
    var msgEl = nstbb.querySelector('[data-nstbb-savings-msg]');
    if (msgEl) msgEl.classList.toggle('is-hidden', saveR <= 0);

    // NSBB's button text (row line total) wins over NSTBB's slot-0 total
    if (typeof nsbb._updateButtonText === 'function') nsbb._updateButtonText();
  }

  // ─────────────────────────────────────────────────────────────
  //  NBB state → SPL visual sync
  // ─────────────────────────────────────────────────────────────

  /**
   * Check the SPL variant radio matching variantId in both sub and one-time columns.
   * We set .checked directly (no dispatchEvent) to avoid re-triggering SPL's own listener.
   */
  function syncVariantToSPL(spl, variantId) {
    if (!variantId) return;
    var id = String(variantId);
    spl.querySelectorAll('.selling-plans-alt__variant-input').forEach(function (inp) {
      inp.checked = inp.dataset.variantId === id || inp.value === id;
    });
  }

  /**
   * Check the SPL delivery radio matching planId and ensure the delivery row is visible.
   */
  function syncDeliveryToSPL(spl, planId) {
    if (!planId) return;
    var id = String(planId);
    spl.querySelectorAll('.selling-plans-alt__delivery-input').forEach(function (inp) {
      inp.checked = inp.dataset.sellingPlanId === id || inp.value === id;
    });
    var delivery = spl.querySelector('.selling-plans-alt__delivery');
    // force-60-day-sub hides delivery via CSS; don't fight it with inline styles
    if (delivery && !spl.classList.contains('selling-plans-alt--force-60-day')) {
      delivery.style.display = 'flex';
    }
  }

  /**
   * Update SPL's CSS classes and radio state to match isSub without dispatching
   * change events (avoids re-triggering SPL's own change handler in a loop).
   */
  function syncPlanTypeToSPL(spl, isSub) {
    var subOpt = spl.querySelector('.selling-plans-alt__option--subscription');
    var otOpt  = spl.querySelector('.selling-plans-alt__option--onetime');
    var containers = getSPLVariantContainers(spl);
    var delivery = spl.querySelector('.selling-plans-alt__delivery');

    if (subOpt) subOpt.classList.toggle('is-selected', isSub);
    if (otOpt)  otOpt.classList.toggle('is-selected', !isSub);
    spl.classList.toggle('is-subscription-selected', isSub);

    var force60 = spl.classList.contains('selling-plans-alt--force-60-day');
    if (delivery) {
      if (force60) {
        delivery.style.display = 'none';
      } else {
        delivery.style.display = isSub ? 'flex' : 'none';
      }
    }
    if (containers.subscriptionVariants) {
      containers.subscriptionVariants.style.display = force60 ? 'none' : isSub ? '' : 'none';
    }
    if (containers.onetimeVariants) {
      containers.onetimeVariants.style.display = force60 || isSub ? 'none' : '';
    }

    // Silently update radio checked state — no dispatchEvent
    var subRadio = subOpt && subOpt.querySelector('.selling-plans-alt__radio-input');
    var otRadio  = otOpt  && otOpt.querySelector('.selling-plans-alt__radio-input');
    if (subRadio) subRadio.checked = isSub;
    if (otRadio)  otRadio.checked  = !isSub;
  }

  // ─────────────────────────────────────────────────────────────
  //  SPL state → NBB visual + form sync
  // ─────────────────────────────────────────────────────────────

  /**
   * Highlight the NBB variant card matching variantId, then refresh NBB's prices,
   * button text, and resolve the canonical selling_plan_id for the new variant.
   */
  function syncVariantToNBB(nbb, variantId) {
    if (!variantId) return;
    var id = String(variantId);
    nbb.querySelectorAll('.nbb__variant-option').forEach(function (card) {
      card.classList.toggle('is-selected', card.dataset.variantId === id);
    });
    if (typeof nbb._updateBoxPrices    === 'function') nbb._updateBoxPrices();
    if (typeof nbb._updateButtonText   === 'function') nbb._updateButtonText();
    if (typeof nbb._resolveAndSyncPlanId === 'function') nbb._resolveAndSyncPlanId();
  }

  /**
   * Select the NBB delivery option matching planId, force subscription plan type
   * active, and refresh NBB's prices + canonical selling_plan_id.
   */
  function syncDeliveryToNBB(nbb, planId) {
    if (!planId) return;
    var id = String(planId);
    var matched = false;
    nbb.querySelectorAll('.nbb__delivery-option').forEach(function (opt) {
      var hit = opt.dataset.sellingPlanId === id;
      opt.classList.toggle('is-selected', hit);
      var radio = opt.querySelector('.nbb__delivery-radio-input');
      if (radio) radio.checked = hit;
      if (hit) matched = true;
    });

    if (matched) {
      var subBox = nbb.querySelector('[data-nbb-plan="sub"]');
      var otBox  = nbb.querySelector('[data-nbb-plan="ot"]');
      if (subBox) subBox.classList.add('is-selected');
      if (otBox)  otBox.classList.remove('is-selected');
      if (typeof nbb._updateVariantCardPriceDisplay === 'function') nbb._updateVariantCardPriceDisplay();
      if (typeof nbb._updateBoxPrices              === 'function') nbb._updateBoxPrices();
      if (typeof nbb._updateButtonText             === 'function') nbb._updateButtonText();
      if (typeof nbb._resolveAndSyncPlanId         === 'function') nbb._resolveAndSyncPlanId();
    }
  }

  /**
   * Switch NBB's plan type (sub/ot), refresh prices, and write the correct
   * selling_plan value to NBB's hidden input (empty string for one-time).
   */
  function syncPlanTypeToNBB(nbb, isSub) {
    var subBox = nbb.querySelector('[data-nbb-plan="sub"]');
    var otBox  = nbb.querySelector('[data-nbb-plan="ot"]');
    if (subBox) subBox.classList.toggle('is-selected', isSub);
    if (otBox)  otBox.classList.toggle('is-selected', !isSub);

    if (typeof nbb._updateVariantCardPriceDisplay === 'function') nbb._updateVariantCardPriceDisplay();
    if (typeof nbb._updateBoxPrices              === 'function') nbb._updateBoxPrices();
    if (typeof nbb._updateButtonText             === 'function') nbb._updateButtonText();

    if (isSub) {
      if (typeof nbb._resolveAndSyncPlanId === 'function') nbb._resolveAndSyncPlanId();
    } else {
      if (typeof nbb._writeSellingPlanInput === 'function') nbb._writeSellingPlanInput('');
    }
  }

  // ─────────────────────────────────────────────────────────────
  //  Helpers
  // ─────────────────────────────────────────────────────────────

  function getSPLVariantContainers(spl) {
    var subOpt = spl.querySelector('.selling-plans-alt__option--subscription');
    var otOpt  = spl.querySelector('.selling-plans-alt__option--onetime');
    var subscriptionVariants =
      (subOpt && subOpt.querySelector('.selling-plans-alt__variants:not(.selling-plans-alt__variants--onetime)')) ||
      spl.querySelector('.selling-plans-alt__variants:not(.selling-plans-alt__variants--onetime)');
    var onetimeVariants =
      (otOpt && otOpt.querySelector('.selling-plans-alt__variants--onetime')) ||
      spl.querySelector('.selling-plans-alt__variants--onetime');
    return { subscriptionVariants: subscriptionVariants, onetimeVariants: onetimeVariants };
  }

  // ─────────────────────────────────────────────────────────────
  //  Init — wait for custom elements to be connected
  // ─────────────────────────────────────────────────────────────

  function boot() {
    // Double rAF: first frame lets the browser process newly deferred scripts
    // (customElements.define), second frame ensures connectedCallback has run.
    requestAnimationFrame(function () {
      requestAnimationFrame(initPairs);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
